import React, {PureComponent} from "react";
import {connect, DispatchProp} from "react-redux";
import {IApplicationState} from "../../store";
import {IAnimationSettings} from "../../store/settings/types";
import {cssHelper} from "../../shared/utils/cssHelper";
import pad from "../../shared/utils/pad";
import styles from "./level-completed.module.css";
import icons from "../../shared/styles/icons.module.css";
import animations from "../../shared/styles/animations.module.css";
import playBackground from "../../shared/sounds/playBackground";
import {loadScene} from "../../store/routing/actions";
import {TimelineLite, Power1, Animation} from "gsap";
import renderText from "../../shared/components/renderText";
import playSound from "../../shared/sounds/playSound";
import i18n from "../../shared/i18n";
import AppStorePanel from "../../shared/components/app-store-panel/AppStorePanel";

interface ILevelCompletedSceneOptions {
    levelTime: number;
    completionPercent: number;
    rightAnswersPercent: number;
    totalScore: number;
    stars: number;
    animation: IAnimationSettings;
}

class LevelCompletedScene extends PureComponent<ILevelCompletedSceneOptions & DispatchProp> {
    private readonly container: React.RefObject<HTMLDivElement>;
    private readonly enteredDate: number;
    private activeTween?: Animation;

    constructor(props: ILevelCompletedSceneOptions & DispatchProp, context: any) {
        super(props, context);

        this.container = React.createRef();
        this.enteredDate = new Date().getTime();
    }

    render() {
        const {levelTime, completionPercent, rightAnswersPercent, totalScore, stars} = this.props;

        const seconds = Math.floor((levelTime / 1000) % 60);
        const minutes = Math.floor((levelTime / (60 * 1000)) % 60);
        const hours = Math.floor(levelTime / (60 * 60 * 1000));

        return (
            <div className={styles.overlay} onClick={() => this.close()}>
                <button className={cssHelper(styles.close_button, icons.close).toString()} onClick={() => this.close()}>✖</button>

                <div className={styles.container} ref={this.container}>
                    <header className={styles.header}>
                        {i18n("gameplay.levelresolution.header")}
                    </header>

                    <div className={styles.star_container}>
                        <i className={cssHelper(styles.star).add(styles.star_disabled, stars < 1 ).toString()}/>
                        <i className={cssHelper(styles.star).add(styles.star_disabled, stars < 2 ).toString()}/>
                        <i className={cssHelper(styles.star).add(styles.star_disabled, stars < 3 ).toString()}/>
                    </div>

                    <div className={styles.score}>
                        {i18n("gameplay.levelresolution.scoreprefix")} {totalScore}
                    </div>

                    <div className={styles.stats_container}>
                        <div className={styles.stats}>
                            <div className={styles.stats_value}>
                                {Math.round(completionPercent * 100)}%
                            </div>
                            <div className={styles.stats_description}>
                                {i18n("gameplay.levelresolution.completionprecentage")}
                            </div>
                        </div>
                        <div className={styles.stats}>
                            <div className={styles.stats_value}>
                                {Math.round(rightAnswersPercent * 100)}%
                            </div>
                            <div className={styles.stats_description}>
                                {i18n("gameplay.levelresolution.correctanswers")}
                            </div>
                        </div>
                        <div className={styles.stats}>
                            <div className={styles.stats_value}>
                                {pad(hours)}:{pad(minutes)}:{pad(seconds)}
                            </div>
                            <div className={styles.stats_description}>
                                {i18n("gameplay.levelresolution.leveltime")}
                            </div>
                        </div>
                    </div>

                    <div className={styles.app_store_panel_wrapper}>
                        <AppStorePanel/>
                    </div>

                    {renderText(i18n("dialog.taptocontinue"), 26, 32, 15, 30, cssHelper(styles.tap_to_continue, animations.blink).toString())}
                </div>
            </div>
        );
    }

    componentDidMount(): void {
        playBackground("level-complete");
        playSound("level-complete");

        if (!this.props.animation.enabled || !this.container.current)
            return;

        const stars = this.container.current.getElementsByClassName(styles.star);
        const children = this.container.current.childNodes;

        this.activeTween = new TimelineLite()
            .staggerFrom(children, 1, {
                opacity: 0,
                ease: Power1.easeInOut
            }, 0.25, "first")
            .staggerFrom(stars, 0.2, {
                scale: 0,
                autoAlpha: 0,
                delay: 0.75,
                ease: Power1.easeInOut,
                onComplete: () => playSound("mouth-pop"),
            }, 0.25, "first")
            .timeScale(this.props.animation.timeScale);
    }

    componentWillUnmount(): void {
        this.activeTween && this.activeTween.kill();
    }

    private close() {
        if (new Date().getTime() < this.enteredDate + 2000)
            return;

        this.props.dispatch(loadScene.request('LevelSelection'));
    }
}

export default connect(({ settings, game }: IApplicationState) => {
    const progress = game.levelMapProgress[game.levelInProgress];

    return {
        levelTime: progress.completeLevelTime - progress.startLevelTime,
        completionPercent: progress.completionPercent,
        rightAnswersPercent: progress.rightAnswersPercent,
        totalScore: progress.totalScore,
        stars: progress.stars,
        animation: settings.animation,
}
})(LevelCompletedScene);